/**
 * i18n routing helpers.
 *
 * URL structure:
 *   NL (default) → / , /products , /cart
 *   EN/DE/FR/ES → /en/products , /de/products , /fr/products , /es/products
 *
 * Production canonical domain = https://stainlesspipeclamps.store
 * Preview & local dev simply use window.location.origin.
 */

export const SUPPORTED_LANGS = ["nl", "en", "de", "fr", "es", "it", "pl", "pt", "sv", "da"];
export const DEFAULT_LANG = "nl";
export const PRODUCTION_DOMAIN = "https://stainlesspipeclamps.store";

const LANG_REGEX = /^\/(en|de|fr|es|it|pl|pt|sv|da)(\/|$)/i;

/** Extract the active language from a pathname (defaults to "nl"). */
export function getLangFromPath(pathname) {
  const m = (pathname || "/").match(LANG_REGEX);
  return m ? m[1].toLowerCase() : DEFAULT_LANG;
}

/** Strip the language prefix from a pathname. Returns "/" for empty paths. */
export function stripLang(pathname) {
  const p = (pathname || "/").replace(LANG_REGEX, "/");
  return p === "" ? "/" : p;
}

/** Prefix a (lang-less) path with the given language. */
export function withLang(path, lang) {
  const clean = stripLang(path || "/");
  if (lang === DEFAULT_LANG) return clean;
  if (clean === "/") return `/${lang}`;
  return `/${lang}${clean}`;
}

/** Pick the canonical origin: production domain in prod, current origin elsewhere. */
export function getOrigin() {
  if (typeof window === "undefined") return PRODUCTION_DOMAIN;
  const host = window.location.hostname || "";
  if (host === "stainlesspipeclamps.store" || host === "www.stainlesspipeclamps.store") {
    return PRODUCTION_DOMAIN;
  }
  return window.location.origin;
}

/**
 * Build canonical + alternate URLs for the current path & language.
 * Returns:
 *   { canonical, alternates: [{ hreflang, href }, ...] }
 * Alternates always include all 10 languages + x-default.
 */
export function buildSeoUrls(pathname, lang) {
  const origin = getOrigin();
  const cleanPath = stripLang(pathname || "/");
  const canonical = `${origin}${withLang(cleanPath, lang || DEFAULT_LANG)}`;
  const alternates = SUPPORTED_LANGS.map((l) => ({
    hreflang: l,
    href: `${origin}${withLang(cleanPath, l)}`,
  }));
  // x-default → the NL (default) version, signalling to Google to fall back to it.
  alternates.push({
    hreflang: "x-default",
    href: `${origin}${withLang(cleanPath, DEFAULT_LANG)}`,
  });
  return { canonical, alternates };
}
